import React, { useEffect, useState, useMemo } from 'react';
import { fetchCurrentPrices } from '../services/marketService';

interface CryptoTickerProps {
  theme: 'dark' | 'light';
}

interface TickerItem {
  symbol: string;
  price: number;
  change: number;
}

const TOP_SYMBOLS = [
  'BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'XRPUSDT', 
  'DOGEUSDT', 'ADAUSDT', 'AVAXUSDT', 'TRXUSDT', 'DOTUSDT',
  'LINKUSDT', 'MATICUSDT', 'LTCUSDT', 'BCHUSDT', 'NEARUSDT',
  'UNIUSDT', 'APTUSDT', 'FILUSDT', 'ATOMUSDT', 'IMXUSDT'
];

const CryptoTicker: React.FC<CryptoTickerProps> = ({ theme }) => {
  const [tickerData, setTickerData] = useState<TickerItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadPrices = async () => {
      try {
        const prices = await fetchCurrentPrices('crypto');
        if (!mounted) return;

        const data: TickerItem[] = [];
        TOP_SYMBOLS.forEach(sym => {
          if (prices[sym]) {
            data.push({
              symbol: sym.replace('USDT', ''),
              price: prices[sym].price,
              change: prices[sym].change24h
            });
          }
        });
        setTickerData(data);
        setLoading(false);
      } catch (e) {
        console.error("Error loading ticker", e);
      }
    };

    loadPrices();
    const interval = setInterval(loadPrices, 5000); // Update every 5s for live feel

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  // Generate a smooth-ish random path for the sparkline visual
  const getSparklinePath = (isPositive: boolean) => {
    // Simple SVG path generation to mimic a chart without fetching history for 20 coins
    if (isPositive) {
      return "M0,15 Q5,18 10,12 T20,10 T30,14 T40,5";
    } else {
      return "M0,5 Q5,2 10,8 T20,10 T30,6 T40,15";
    }
  };

  const textClass = theme === 'dark' ? 'text-slate-300' : 'text-slate-700';

  if (loading) return null;

  return (
    <div className="relative flex-1 h-full overflow-hidden select-none mask-linear-fade w-full ticker-container cursor-default">
      <div className="flex w-max animate-marquee whitespace-nowrap items-center h-full">
        {[...tickerData, ...tickerData].map((item, idx) => { // Duplicate for infinite loop
          const isPositive = item.change >= 0;
          const color = isPositive ? 'text-[#089981]' : 'text-[#f23645]';
          const stroke = isPositive ? '#089981' : '#f23645';
          
          return (
            <div key={`${item.symbol}-${idx}`} className={`flex-shrink-0 inline-flex items-center space-x-3 px-6 py-1 border-r ${theme === 'dark' ? 'border-white/5' : 'border-slate-200/60'}`}>
              <div className="flex items-center space-x-2">
                 <img 
                    src={`https://assets.coincap.io/assets/icons/${item.symbol.toLowerCase()}@2x.png`} 
                    className="w-4 h-4 rounded-full"
                    onError={(e) => e.currentTarget.style.display = 'none'}
                    alt="" 
                 />
                 <span className={`text-[11px] font-black ${textClass}`}>{item.symbol}</span>
              </div>
              
              <span className={`text-[11px] font-mono font-bold ${textClass}`}>
                {item.price < 1 ? item.price.toFixed(4) : item.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>

              <div className="flex items-center space-x-2">
                 <span className={`text-[10px] font-bold ${color}`}>
                    {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
                 </span>
                 <svg width="30" height="15" viewBox="0 0 40 20" fill="none" className="opacity-80">
                    <path 
                        d={getSparklinePath(isPositive)} 
                        stroke={stroke} 
                        strokeWidth="2" 
                        fill="none" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                    />
                 </svg>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CryptoTicker;
